// Product-name index so partial model codes ("sg22", "2218", "tlsg2218") still find products.
// Shopify's own search only matches whole or near-whole words, so we also match against titles here.
const WsSearchIndex = {
  promise: null,
  storageKey: 'ws-search-index-v1',

  load() {
    if (this.promise) return this.promise;
    this.promise = new Promise((resolve) => {
      try {
        const cached = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
        if (cached && Date.now() - cached.time < 30 * 60 * 1000) return resolve(this.prepare(cached.items));
      } catch (e) {}
      this.fetchPages(1, []).then((items) => {
        try {
          sessionStorage.setItem(this.storageKey, JSON.stringify({ time: Date.now(), items }));
        } catch (e) {}
        resolve(this.prepare(items));
      }).catch(() => resolve([]));
    });
    return this.promise;
  },

  fetchPages(page, items) {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return fetch(`${root}collections/all?view=ws-index&page=${page}`)
      .then((response) => response.json())
      .then((pageItems) => {
        items = items.concat(pageItems);
        if (pageItems.length === 250 && page < 10) return this.fetchPages(page + 1, items);
        return items;
      });
  },

  normalize(text) {
    return text.toLowerCase().replace(/[^a-z0-9]+/g, '');
  },

  prepare(items) {
    return items.map((item) => ({
      ...item,
      compact: this.normalize(item.t),
      words: item.t.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean),
    }));
  },

  search(items, query, limit) {
    const compactQuery = this.normalize(query);
    if (compactQuery.length < 2) return [];
    const tokens = query.toLowerCase().split(/\s+/).map((t) => this.normalize(t)).filter(Boolean);

    const results = [];
    items.forEach((item) => {
      const allTokens = tokens.every((token) => item.compact.includes(token));
      if (!allTokens && !item.compact.includes(compactQuery)) return;

      let score = 0;
      tokens.forEach((token) => {
        if (item.words.includes(token)) score += 5;
        else if (item.words.some((word) => word.startsWith(token))) score += 3;
        else score += 1;
      });
      if (item.compact.includes(compactQuery)) score += 2;
      if (item.words.some((word) => word.startsWith(compactQuery))) score += 4;
      results.push({ item, score });
    });

    results.sort((a, b) => b.score - a.score || a.item.t.length - b.item.t.length);
    return results.slice(0, limit).map((result) => result.item);
  },
};

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('focus', () => WsSearchIndex.load(), { once: true });
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const shopifyResults = fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`,
      {
        signal: this.abortController.signal,
      }
    ).then((response) => {
      if (!response.ok) {
        var error = new Error(response.status);
        this.close();
        throw error;
      }

      return response.text();
    });

    Promise.all([shopifyResults, WsSearchIndex.load()])
      .then(([text, indexItems]) => {
        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');
        this.addIndexMatches(section, searchTerm, indexItems);
        const resultsMarkup = section.innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  // Replace the product list with title matches from WsSearchIndex (if any were found).
  addIndexMatches(section, searchTerm, indexItems) {
    const matches = WsSearchIndex.search(indexItems, searchTerm, 10);
    const results = section && section.querySelector('#predictive-search-results');
    if (!matches.length || !results) return;

    const escapeHtml = (text) =>
      String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

    let wrapper = section.querySelector('#predictive-search-results-groups-wrapper');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.id = 'predictive-search-results-groups-wrapper';
      wrapper.className =
        'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions';
      results.prepend(wrapper);
    }
    wrapper.classList.remove('predictive-search__results-groups-wrapper--no-products');

    let group = section.querySelector('#product-suggestions');
    if (!group) {
      group = document.createElement('div');
      group.className = 'predictive-search__result-group';
      group.id = 'product-suggestions';
      wrapper.appendChild(group);
    }

    let list = section.querySelector('#predictive-search-results-products-list');
    if (!list) {
      const container = document.createElement('div');
      container.innerHTML =
        '<h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">Products</h2>' +
        '<ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products"></ul>';
      group.prepend(container);
      list = container.querySelector('ul');
    }

    list.innerHTML = matches
      .map(
        (item, index) => `<li id="predictive-search-option-product-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
          <a href="${escapeHtml(item.u)}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
            ${item.i ? `<img class="predictive-search__image" src="${escapeHtml(item.i)}" alt="" width="50" style="height:auto">` : ''}
            <div class="predictive-search__item-content predictive-search__item-content--centered">
              <p class="predictive-search__item-heading h5">${escapeHtml(item.t)}</p>
            </div>
          </a>
        </li>`
      )
      .join('');

    const liveRegion = section.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegion) liveRegion.textContent = `${matches.length} products`;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);

// "Best matches" row on the search results page, filled from WsSearchIndex with the theme's own product cards.
class WsBestMatches extends HTMLElement {
  connectedCallback() {
    const terms = this.dataset.terms || '';
    const list = this.querySelector('ul');
    if (!terms || !list) return;

    WsSearchIndex.load().then((items) => {
      const matches = WsSearchIndex.search(items, terms, 25);
      // Generic words (e.g. "tapo") match too many products to be useful here; Shopify's grid covers those
      if (!matches.length || matches.length > 24) return;

      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      Promise.all(
        matches.map((item) =>
          fetch(`${root}products/${item.h}?section_id=ws-product-card`)
            .then((response) => (response.ok ? response.text() : ''))
            .then((text) => {
              const card = new DOMParser()
                .parseFromString(text, 'text/html')
                .querySelector('.shopify-section');
              return card ? card.innerHTML.trim() : '';
            })
            .catch(() => '')
        )
      ).then((cards) => {
        const html = cards.filter(Boolean).map((card) => `<li class="grid__item">${card}</li>`).join('');
        if (!html) return;
        list.innerHTML = html;
        this.copyGridStyles();
        this.hidden = false;
        if (this.dataset.empty === 'true') {
          document.querySelectorAll('.template-search__header p[role="status"]').forEach((el) => (el.hidden = true));
        }
      });
    });
  }

  // The theme styles product cards only inside #product-grid; reuse those rules so these cards look the same.
  copyGridStyles() {
    const css = [];
    Array.from(document.styleSheets).forEach((sheet) => {
      let rules;
      try {
        rules = sheet.cssRules;
      } catch (e) {
        return;
      }
      Array.from(rules || []).forEach((rule) => {
        if (rule.selectorText && rule.selectorText.includes('#product-grid')) {
          css.push(rule.cssText.split('#product-grid').join('ws-best-matches'));
        }
      });
    });
    if (!css.length) return;
    const style = document.createElement('style');
    style.textContent = css.join('\n');
    this.appendChild(style);
  }
}

customElements.define('ws-best-matches', WsBestMatches);
